import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useMemo, useState } from "react";
import { courseList } from "../../api/Courses";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { DeleteSyllabus, FetchSyllabus } from "../../api/SyllabusService";
import AcademicHeader from "../Academic/AcademicHeader";
import { subjectList } from "../../api/Subjects";
import Pagination from "../components/Pagination";

// Small palette so subject/course badges feel distinct but stay in the
// existing "bg-label-*" language already used elsewhere in the app.
const BADGE_VARIANTS = [
  "bg-label-primary",
  "bg-label-success",
  "bg-label-warning",
  "bg-label-info",
  "bg-label-danger",
  "bg-label-secondary",
];

const badgeVariantFor = (key) => {
  if (!key) return BADGE_VARIANTS[0];
  const code = String(key)
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return BADGE_VARIANTS[code % BADGE_VARIANTS.length];
};

export default function SyllabusView() {
  const [pagination, setPagination] = useState({});
  const hasAccess = usePrivateLink();
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syllabus, setSyllabus] = useState([]);
  const [subjects, setSubject] = useState([]);

  const [filters, setFilters] = useState({
    page: 1,
  });
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  const [search, setSearch] = useState("");
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const storedSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");

    setSessions(storedSessions);

    const activeSession = storedSessions.find(
      (session) => String(session?.status) === "1",
    );

    if (activeSession) {
      setSelectedSession(String(activeSession.id));
    }
  }, []);

  useEffect(() => {
    const userPermissions = localStorage.getItem("user_permissions");

    if (!userPermissions) {
      setPermissions([]);
      return;
    }

    try {
      const parsedPermissions = JSON.parse(userPermissions);
      setPermissions(parsedPermissions?.permissions || []);
    } catch (error) {
      setPermissions([]);
    }
  }, []);

  const courseFetch = async () => {
    setLoading(true);

    try {
      const response = await courseList();

      if (response?.data?.status) {
        setCourses(response?.data?.data || []);
      } else {
        setCourses([]);
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const subjectFetch = async () => {
    setLoading(true);

    try {
      const response = await subjectList();

      if (response?.data?.status) {
        setSubject(response?.data?.data || []);
      } else {
        setSubject([]);
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    courseFetch();
    subjectFetch();
  }, []);

  const handleCourseChange = (e) => {
    const courseId = e.target.value;

    setSelectedCourse(courseId);
    setSelectedSubject("");

    if (courseId) {
      const selectedCourseData = courses.find(
        (course) => String(course.id) === String(courseId),
      );

      setSubject(selectedCourseData?.subjects_list || []);
    } else {
      subjectFetch();
    }

    setSyllabus([]);
  };

  const handleSessionChange = (e) => {
    setSelectedSession(e.target.value);
    setSyllabus([]);
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  const syllabusFetch = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      if (selectedSession) addUrl += `&session_id=${selectedSession}`;
      if (selectedCourse) addUrl += `&course_id=${selectedCourse}`;
      if (selectedSubject) addUrl += `&subject_id=${selectedSubject}`;
      if (search) addUrl += `&search=${encodeURIComponent(search)}`;
      if (filters.page) addUrl += `&page=${filters.page}`;

      const response = await FetchSyllabus(addUrl);

      if (response?.data?.status) {
        const result = response.data.data;

        setSyllabus(result?.data || []);
        setPagination(result);
      } else {
        setSyllabus([]);
        setPagination({});
      }
    } catch (error) {
      setSyllabus([]);
      setPagination({});
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      syllabusFetch();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    selectedSession,
    selectedCourse,
    selectedSubject,
    search,
    filters.page,
  ]);

  const handleSyllabusDelete = async (id, title) => {
    const confirmation = await Swal.fire({
      title: "Delete this syllabus?",
      html: `<span class="text-muted">This will permanently remove <strong>${title || "this entry"
        }</strong>. You can't undo this.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) return;

    setLoading(true);

    try {
      const response = await DeleteSyllabus(id);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
        await syllabusFetch();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSelectedCourse("");
    setSelectedSubject("");
    setSearch("");
    subjectFetch();
  };
  const canManageSyllabus = hasAccess("/syllabus/add");
  const canUpdateSyllabus = hasAccess("/syllabus/update");
  const canDeleteSyllabus = hasAccess("/syllabus/delete");

  const hasActiveFilters = Boolean(selectedCourse || selectedSubject || search);

  const totalDays = useMemo(
    () =>
      syllabus.reduce(
        (sum, item) => sum + (Number(item?.duration_day) || 0),
        0,
      ),
    [syllabus],
  );

  return (
    <>
      {/* Loading */}
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      {/* Header */}
                      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-book-open-line fs-4" />
                          </div>
                          <div>
                            <h4 className="text-primary m-0">Syllabus</h4>
                            <small className="text-muted">
                              Course &amp; subject wise syllabus
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {!loading && (
                            <span className="badge bg-label-secondary rounded-pill px-3 py-2">
                              {syllabus.length}{" "}
                              {syllabus.length === 1 ? "record" : "records"}
                              {totalDays > 0
                                ? ` · ${totalDays} days total`
                                : ""}
                            </span>
                          )}

                          {canManageSyllabus && (
                            <Link
                              to="/syllabus/add"
                              className="btn btn-primary btn-sm"
                            >
                              <i className="ri-add-line me-1" />
                              Add Syllabus
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Filters */}
                      <div className="card-body pb-2">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="text-uppercase text-muted small fw-semibold">
                            <i className="ri-filter-3-line me-1" />
                            Filters
                          </span>

                          {hasActiveFilters && (
                            <button
                              type="button"
                              className="btn btn-sm btn-text-secondary text-decoration-underline"
                              onClick={handleResetFilters}
                            >
                              Clear filters
                            </button>
                          )}
                        </div>

                        <div className="row g-2">
                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedSession}
                                onChange={handleSessionChange}
                              >
                                <option value="">All Sessions</option>
                                {sessions?.map((session) => (
                                  <option key={session?.id} value={session?.id}>
                                    {session?.session}
                                    {String(session?.status) === "1"
                                      ? " : Active"
                                      : " : Inactive"}
                                  </option>
                                ))}
                              </select>
                              <label>Session</label>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedCourse}
                                onChange={handleCourseChange}
                              >
                                <option value="">All Courses</option>
                                {courses?.map((course) => (
                                  <option key={course?.id} value={course?.id}>
                                    {course?.title}
                                  </option>
                                ))}
                              </select>
                              <label>Course</label>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedSubject}
                                onChange={handleSubjectChange}
                              >
                                <option value="">All Subjects</option>
                                {subjects?.map((sub) => (
                                  <option key={sub?.id} value={sub?.id}>
                                    {sub?.name}
                                  </option>
                                ))}
                              </select>
                              <label>Subject</label>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                className="form-control"
                                id="search"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                              />
                              <label htmlFor="search">
                                <i className="ri-search-line me-1" />
                                Search Topic
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="card-body pt-2">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 48 }}>#</th>
                                <th>Topic</th>
                                <th>Course</th>
                                <th>Subject</th>
                                <th>Duration</th>
                                <th>Description</th>
                                <th className="text-center">Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {syllabus?.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="border-0">
                                    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                                      <div
                                        className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center mb-3"
                                        style={{ width: 64, height: 64 }}
                                      >
                                        <i className="ri-file-list-3-line fs-2 text-muted" />
                                      </div>
                                      <h6 className="mb-1">
                                        No syllabus found
                                      </h6>
                                      <p
                                        className="text-muted small mb-3"
                                        style={{ maxWidth: 320 }}
                                      >
                                        {hasActiveFilters
                                          ? "No entries match your current filters. Try clearing them or searching a different topic."
                                          : "Nothing has been added yet for this selection."}
                                      </p>
                                      {hasActiveFilters ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={handleResetFilters}
                                        >
                                          Clear filters
                                        </button>
                                      ) : (
                                        canManageSyllabus && (
                                          <Link
                                            to="/syllabus/add"
                                            className="btn btn-sm btn-primary"
                                          >
                                            <i className="ri-add-line me-1" />
                                            Add Syllabus
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                syllabus.map((item, index) => (
                                  <tr key={item?.id}>
                                    <td className="text-center text-muted">
                                      {index + 1}
                                    </td>
                                    <td>
                                      <span className="fw-semibold text-primary">
                                        {item?.title || "N/A"}
                                      </span>
                                    </td>
                                    <td>
                                      {item?.course?.title ? (
                                        <span
                                          className={`badge ${badgeVariantFor(
                                            item?.course?.id,
                                          )} rounded-pill`}
                                        >
                                          {item.course.title}
                                        </span>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      {item?.subject?.name ? (
                                        <span
                                          className={`badge ${badgeVariantFor(
                                            item?.subject?.id,
                                          )} rounded-pill`}
                                        >
                                          {item.subject.name}
                                        </span>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      <span className="badge bg-label-info">
                                        <i className="ri-time-line me-1" />
                                        {item?.duration_day || 0} Days
                                      </span>
                                    </td>
                                    <td style={{ maxWidth: 260 }}>
                                      <span
                                        className="text-muted small d-inline-block text-truncate"
                                        style={{ maxWidth: 260 }}
                                        title={item?.description || ""}
                                      >
                                        {item?.description || "-"}
                                      </span>
                                    </td>

                                    <td className="text-center">
                                      <div className="d-flex justify-content-center align-items-center gap-2">
                                        {canUpdateSyllabus && (
                                          <Link
                                            to="/syllabus/update"
                                            state={{ syllabusItem: item }}
                                            className="btn btn-sm btn-outline-primary"
                                            title="Edit"
                                            aria-label="Edit syllabus"
                                          >
                                            <i className="ri-pencil-line" />
                                          </Link>
                                        )}

                                        {canDeleteSyllabus && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            title="Delete"
                                            aria-label="Delete syllabus"
                                            onClick={() =>
                                              handleSyllabusDelete(
                                                item?.id,
                                                item?.title,
                                              )
                                            }
                                          >
                                            <i className="ri-delete-bin-line" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                          <Pagination
                            data={pagination}
                            setFilters={setFilters}
                            filters={filters}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
